<script setup>
/**
 * DataTable 通用表格組件
 *
 * 支援兩種分頁模式：外部分頁（向後相容）和內建分頁（推薦新頁面使用）
 * 詳細使用說明請參考 docs/DataTable.md
 */

import { ref, computed } from 'vue';
import { ReloadOutlined, ColumnHeightOutlined } from '@ant-design/icons-vue';
import usePagination from '@/composables/usePagination';

const props = defineProps({
    title: {
        type: String,
        required: true
    },
    columns: {
        type: Array,
        required: true
    },
    dataSource: {
        type: Array,
        required: true
    },
    pagination: {
        type: Object,
        default: null
    },
    scroll: {
        type: Object,
        default: () => ({ x: 1500 })
    },
    // 是否使用內建分頁
    useBuiltInPagination: {
        type: Boolean,
        default: false
    },
    // 分頁配置選項
    paginationOptions: {
        type: Object,
        default: () => ({})
    },
    // API 呼叫函數
    onPageChange: {
        type: Function,
        default: null
    }
});

const emit = defineEmits(['refresh', 'change']);

// 表格密度設置
const tableSize = ref('middle');

const handleSizeChange = (size) => {
    tableSize.value = size;
};

// 內建分頁處理
const {
    pagination: builtInPagination,
    updatePagination,
    resetPagination
} = usePagination({
    ...props.paginationOptions,
    onChange: async (page, pageSize) => {
        if (props.onPageChange) {
            await props.onPageChange(page, pageSize);
        }
    },
    onShowSizeChange: async (current, size) => {
        if (props.onPageChange) {
            await props.onPageChange(1, size);
        }
    }
});

// 計算最終使用的分頁配置
const finalPagination = computed(() => {
    if (props.useBuiltInPagination) {
        return builtInPagination.value;
    }
    return props.pagination;
});

// 表格變化處理函數
const handleTableChange = (pagination, filters, sorter) => {
    if (props.useBuiltInPagination) {
        // 內建分頁模式下，分頁變更由 usePagination 自動處理
        return;
    }
    // 外部分頁模式下，發送事件給父組件
    emit('change', { pagination, filters, sorter });
};

// 暴露內建分頁方法給父組件
const updateBuiltInPagination = (newPagination) => {
    if (props.useBuiltInPagination) {
        updatePagination(newPagination);
    }
};

const resetBuiltInPagination = () => {
    if (props.useBuiltInPagination) {
        resetPagination();
    }
};

// 暴露方法給父組件
defineExpose({
    updatePagination: updateBuiltInPagination,
    resetPagination: resetBuiltInPagination
});
</script>

<template>
    <a-card :bordered="false" class="admin-panel admin-data-table">
        <div class="admin-data-table__toolbar">
            <div class="admin-data-table__title-wrap">
                <span class="admin-data-table__title">{{ title }}</span>
                <slot name="titleContent"></slot>
            </div>
            <a-space>
                <slot name="titleExtra"></slot>

                <a-tooltip title="刷新">
                    <a-button @click="$emit('refresh')">
                        <template #icon><reload-outlined /></template>
                    </a-button>
                </a-tooltip>
                <a-tooltip title="密度">
                    <a-dropdown>
                        <a-button>
                            <template #icon>
                                <column-height-outlined />
                            </template>
                        </a-button>
                        <template #overlay>
                            <a-menu
                                @click="({ key }) => handleSizeChange(key)"
                                :selectedKeys="[tableSize]"
                            >
                                <a-menu-item key="default">寬鬆</a-menu-item>
                                <a-menu-item key="middle">中等</a-menu-item>
                                <a-menu-item key="small">緊湊</a-menu-item>
                            </a-menu>
                        </template>
                    </a-dropdown>
                </a-tooltip>
            </a-space>
        </div>

        <a-table
            :columns="columns"
            :data-source="dataSource"
            :pagination="finalPagination"
            :size="tableSize"
            row-key="id"
            :scroll="scroll"
            @change="handleTableChange"
            class="custom-table admin-table-surface"
        >
            <template
                v-for="(_, name) in $slots"
                :key="name"
                #[name]="slotData"
            >
                <slot :name="name" v-bind="slotData"></slot>
            </template>
        </a-table>
    </a-card>
</template>

<style scoped>
.admin-data-table__toolbar {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    justify-content: space-between;
    gap: 12px 16px;
    margin-bottom: 16px;
    padding-bottom: 14px;
    border-bottom: 1px solid rgba(226, 232, 240, 0.95);
}

.admin-data-table__title-wrap {
    display: flex;
    align-items: center;
    gap: 8px;
    min-width: 0;
}

.admin-data-table__title {
    font-size: 16px;
    font-weight: 600;
    color: #0f172a;
    letter-spacing: 0.01em;
}

/*
 * 強制修改 Ant Design DataTable 樣式
 * 
 * 注意：這些樣式是為了覆寫 Ant Design 的預設樣式，
 * 解決特定 UI/UX 問題。請謹慎修改，確保不會影響
 * 其他表格元件的正常顯示。
 */

/* 固定列樣式：確保右側固定列有正確的背景色和陰影效果 */
.custom-table :deep(.ant-table-cell-fix-right) {
    background-color: #ffffff !important;
    box-shadow: -6px 0 6px -4px rgba(0, 0, 0, 0.12) !important;
}

/* 分頁元件樣式：強制顯示分頁選項，確保下拉選單有足夠寬度 */
.custom-table :deep(.ant-pagination-options) {
    display: inline-block !important;
}

.custom-table :deep(.ant-pagination-options-size-changer) {
    display: inline-block !important;
}

.custom-table :deep(.ant-select-selector) {
    min-width: 100px !important;
}

.custom-table :deep(.ant-select-selection-item) {
    text-align: center !important;
}

/* 滾動條樣式：隱藏橫向滾動條，保持滾動功能但隱藏視覺元素 */
.custom-table :deep(.ant-table-body) {
    overflow-x: auto !important;
}

.custom-table :deep(.ant-table-body::-webkit-scrollbar) {
    display: none !important;
}

.custom-table :deep(.ant-table-body) {
    -ms-overflow-style: none !important;
    scrollbar-width: none !important;
}

/* 表格容器滾動條：確保表格容器也隱藏滾動條 */
.custom-table :deep(.ant-table) {
    overflow-x: auto !important;
}

.custom-table :deep(.ant-table::-webkit-scrollbar) {
    display: none !important;
}

.custom-table :deep(.ant-table) {
    -ms-overflow-style: none !important;
    scrollbar-width: none !important;
}

/* 全域滾動條隱藏：針對所有可能的滾動容器 */
.custom-table :deep(*) {
    scrollbar-width: none !important;
    -ms-overflow-style: none !important;
}

.custom-table :deep(*::-webkit-scrollbar) {
    display: none !important;
}

/* 響應式設計：移動設備上的分頁樣式優化 */
@media screen and (max-width: 768px) {
    .custom-table :deep(.ant-pagination) {
        display: flex !important;
        align-items: center !important;
        justify-content: center !important;
        flex-wrap: wrap !important;
    }

    .custom-table :deep(.ant-pagination-options) {
        margin-left: 8px !important;
    }
}
</style>
